import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users, Lock, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const T = {
  en: {
    gate_title: 'Members-only area',
    gate_subtitle: 'The Heliophant community is reserved for registered users. Sign up for free in less than a minute, or log in if you already have an account.',
    benefits_title: 'What you get inside',
    benefits: [
      'Discussions with other Heliophant users',
      'Early access to research notes',
      'Direct feedback channel to the team',
    ],
    cta_signup: 'Create free account',
    cta_login: 'I already have an account',
    welcome_title: 'Welcome to the Heliophant community',
    welcome_subtitle: 'You are signed in as',
    coming_soon: 'Community features are launching soon. Stay tuned.',
  },
  el: {
    gate_title: 'Περιοχή μόνο για μέλη',
    gate_subtitle: 'Η κοινότητα του Heliophant είναι διαθέσιμη μόνο σε εγγεγραμμένους χρήστες. Κάντε δωρεάν εγγραφή σε λιγότερο από ένα λεπτό, ή συνδεθείτε αν έχετε ήδη λογαριασμό.',
    benefits_title: 'Τι αποκτάτε μέσα',
    benefits: [
      'Συζητήσεις με άλλους χρήστες του Heliophant',
      'Πρώιμη πρόσβαση σε ερευνητικές σημειώσεις',
      'Άμεσο κανάλι επικοινωνίας με την ομάδα',
    ],
    cta_signup: 'Δημιουργία δωρεάν λογαριασμού',
    cta_login: 'Έχω ήδη λογαριασμό',
    welcome_title: 'Καλώς ήρθατε στην κοινότητα του Heliophant',
    welcome_subtitle: 'Έχετε συνδεθεί ως',
    coming_soon: 'Οι λειτουργίες της κοινότητας θα ξεκινήσουν σύντομα. Μείνετε συντονισμένοι.',
  },
};

const CommunityPage = () => {
  const { user, loading } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = T[language] || T.en;

  const isLoggedIn = user && user !== false;

  return (
    <div className="min-h-screen bg-[#050811] text-white flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16" data-testid="community-page">
        {loading ? (
          <div className="text-center text-white/60 py-20" data-testid="community-loading">…</div>
        ) : !isLoggedIn ? (
          <div
            className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-8 md:p-12 text-center"
            data-testid="community-gate"
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
              <Lock className="w-8 h-8 text-white" />
            </div>

            <h1 className="text-3xl md:text-4xl font-bold mb-3" data-testid="community-gate-title">
              {t.gate_title}
            </h1>
            <p className="text-white/70 leading-relaxed mb-8 max-w-xl mx-auto">
              {t.gate_subtitle}
            </p>

            <div className="bg-white/[0.03] border border-white/10 rounded-xl p-6 mb-8 text-left max-w-md mx-auto">
              <h2 className="text-white font-semibold mb-3 text-sm uppercase tracking-wide text-white/60">
                {t.benefits_title}
              </h2>
              <ul className="space-y-2">
                {t.benefits.map((b) => (
                  <li key={b} className="text-white/80 text-sm flex items-start gap-2">
                    <ArrowRight className="text-cyan-400 mt-0.5 flex-shrink-0" size={16} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                onClick={() => navigate('/register')}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                data-testid="community-cta-signup"
              >
                {t.cta_signup}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/login')}
                className="border-white/20 text-white hover:bg-white/5"
                data-testid="community-cta-login"
              >
                {t.cta_login}
              </Button>
            </div>
          </div>
        ) : (
          <div
            className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-8 md:p-12"
            data-testid="community-welcome"
          >
            <div className="w-16 h-16 mb-6 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
              <Users className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-3" data-testid="community-welcome-title">
              {t.welcome_title}
            </h1>
            <p className="text-white/70 mb-6">
              {t.welcome_subtitle}{' '}
              <span className="text-cyan-400 font-medium">{user.email || user.name}</span>.
            </p>
            <p className="text-white/60 text-sm italic">{t.coming_soon}</p>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default CommunityPage;
